import React, { useEffect } from 'react'
import { Link } from 'react-router'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import {
  TableMultiSelectionWrapper,
  TableMultiSelectionConsumer,
  SelectAllRows,
  SelectRow
} from '../../components/rcm-table/TableMultiSelection'
import { RcmTable } from '../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../components/table/TableComponents'
import DocIcon from '../../components/shared/DocIcon'
import DocState from '../../components/shared/DocState'
import TableDateTimeCell from '../../components/shared/TableDateTimeCell'
import { DocumentTableMenu } from '../browser/DocumentTableMenu'
import { FsiGridItem } from '../browser/FsiGridItem'
import { FsiType, AclLevel } from '../../constants/fsi'

import DueDateIndicator from '../../components/DueDateIndicator'

interface TableProps {
  totalElements: Promise<{ totalElements: number }> | number | null
  isStudyUser?: boolean
  isSiteUser?: boolean
  contextId?: string
  mode?: string
  store?: any
}

const Table: React.FC<TableProps> = ({ totalElements, isStudyUser, isSiteUser, contextId, mode = '', store }) => {
  const context = useServiceContext()
  const tableData = useRcmTableData()
  const translate = context.service.filter.translate
  const isEtmfEnabled = context.service.init.getFeaturesInfo()?.etmfFeature?.show || false

  useEffect(() => {
    // Listen for table refresh events
    const handler = () => tableData.refreshTable()
    // Simulate $rootScope.$on('bctable:updateTable', ...)
    window.addEventListener('bctable:updateTable', handler)
    return () => {
      window.removeEventListener('bctable:updateTable', handler)
    }
  }, [tableData])

  return (
    <TableMultiSelectionWrapper
      isSelectTotalEnabled={true}
      isWorkflow={true}
      texts={{
        selectAllItemsText: 'MULTISELECT_SELECT_ALL_IN_SECTION',
        allItemsSelectedText: 'MULTISELECT_ALL_N_ITEMS_IN_IN_SECTION_SELECTED'
      }}
      getTotalNumberFunction={(_filters) => {
        if (totalElements == null) return Promise.resolve(0)
        if (typeof totalElements === 'number') {
          return Promise.resolve(totalElements)
        }
        // totalElements is a Promise
        return totalElements.then((data: { totalElements: number }) => data.totalElements)
      }}
    >
      <RcmTable
        className="chita-table fsiTable"
        noResultsText={translate('NO_RESULTS_FILTERED')}
        disableOnInitLoad={true}
        simplePaginate={false}
        rowKey={'fsiId'}
        GridItemView={({ row }: { row: any }) => <FsiGridItem row={row?.fsiDetail} />}
        HeadRow={
          <TableHeadRow className="has-actions">
            {mode !== 'missing' && (
              <TableHeadCell className="clipped-cell with-checkbox" id="c6ba22ec-1b80-4166-b541-9e3dba910ab7">
                <SelectAllRows />
              </TableHeadCell>
            )}
            <TableHeadCell sort={'name'}>{translate('NAME')}</TableHeadCell>
            <TableHeadCell sort={'zone'}>{translate('EMTF_ZONE')}</TableHeadCell>
            <TableHeadCell sort={'section'} className="narrow">
              {translate('EMTF_SECTION')}
            </TableHeadCell>
            <TableHeadCell sort={'modifiedAt'} className="narrow">
              {translate('TABLE_TH_UPDATED_ON')}
            </TableHeadCell>
            <TableHeadCell className="narrow actions">{translate('ACTIONS')}</TableHeadCell>
          </TableHeadRow>
        }
        Row={({ row }: { row: any }) => {
          if (!row || !row.fsiDetail) return null
          return (
            <TableRow key={row.fsiId}>
              {mode !== 'missing' && (
                <td className="clipped-cell with-checkbox">
                  <SelectRow row={row.fsiDetail} />
                </td>
              )}
              <td className="name clipped-cell with-thumb">
                <div className="d-flex align-left">
                  <DocIcon fsi={row.fsiDetail} />
                  <div className="ellipsis" title={row.name}>
                    {/* Folder link logic */}
                    {row.fsiDetail.type === FsiType.FOLDER &&
                      row.fsiDetail.acl !== AclLevel.BROWSE &&
                      row.fsiDetail.valid && <Link to={`/browser/${row.fsiDetail.id}`}>{row.name}</Link>}
                    {/* File link logic */}
                    {row.fsiDetail.type !== FsiType.PLACEHOLDER &&
                      row.fsiDetail.type !== FsiType.FOLDER &&
                      row.fsiDetail.acl !== AclLevel.BROWSE &&
                      row.fsiDetail.valid && <Link to={`/show-file/${row.fsiDetail.id}`}>{row.name}</Link>}
                    {/* Plain text fallback */}
                    {row.fsiDetail.type !== FsiType.PLACEHOLDER &&
                      (row.fsiDetail.acl === AclLevel.BROWSE || !row.fsiDetail.valid) && (
                        <span className="whitespaceprewrap" title={row.name}>
                          {row.name}
                        </span>
                      )}
                    {/* Placeholder fallback */}
                    {row.fsiDetail.type === FsiType.PLACEHOLDER && (
                      <span className="whitespaceprewrap" title={row.name}>
                        {row.name}
                        {isEtmfEnabled && (
                          <DueDateIndicator dueDateStatus={row.fsiDetail.dueDateStatus} translate={translate} />
                        )}
                      </span>
                    )}
                  </div>
                  <DocState fsi={row.fsiDetail} />
                </div>
              </td>
              <td className="clipped-cell">
                <div className="d-flex align-left">
                  <div className="ellipsis" title={row.zone || translate('NOT_AVAILABLE')}>
                    {row.zone || translate('NOT_AVAILABLE')}
                  </div>
                </div>
              </td>
              <td className="narrow">{row.section ? row.section : translate('NOT_AVAILABLE')}</td>
              <td className="narrow">
                <TableDateTimeCell val={row.fsiDetail.updatedOn} includeTime />
              </td>
              <td className="narrow updated">
                <div className="actions d-inline-block float-right">
                  <TableMultiSelectionConsumer>
                    {(tableMultiSelection: any) =>
                      tableMultiSelection ? (
                        <DocumentTableMenu
                          item={row.fsiDetail}
                          options={{
                            isPropertiesSearch: mode === 'propertySearch',
                            parentFolderId: null,
                            isStagingUser: true,
                            isStagingArea: true,
                            contextId,
                            filters: store?.formRefStaging?.values?.filter,
                            stagingAreaType: isStudyUser ? 'study' : isSiteUser ? 'site' : '',
                            ...(tableMultiSelection.multiSelectState?.selectedIds
                              ? {
                                  meta: {},
                                  selectedItems: tableData.entries.filter((i: any) =>
                                    tableMultiSelection.multiSelectState.selectedIds.includes(i.id)
                                  )
                                }
                              : null),
                            ...tableMultiSelection.multiSelectState,
                            deselectAll: tableMultiSelection.deselectAll
                          }}
                        />
                      ) : null
                    }
                  </TableMultiSelectionConsumer>
                </div>
              </td>
            </TableRow>
          )
        }}
      />
    </TableMultiSelectionWrapper>
  )
}

export default Table
